import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';

export interface CategoryPill {
    label: string;
    /** When set the pill renders as a link; otherwise a static tag. */
    to?: string;
}

export interface CategoryPillsProps {
    /** Heading above the pill row, e.g. "Popular categories:". */
    label?: string;
    items: CategoryPill[];
    className?: string;
    /** Extra classes for the heading (e.g. a page-specific font size). */
    labelClassName?: string;
    /** Extra classes for the wrapping pill row (e.g. a page-specific gap). */
    wrapClassName?: string;
    /** Extra classes for each pill (e.g. a filled variant instead of the outline). */
    pillClassName?: string;
}

const pillClass =
    'inline-flex items-center justify-center whitespace-nowrap rounded-full border border-current uppercase text-current transition-opacity hover:opacity-70 max-md:px-[2.13vw] max-md:text-[1.83vw] max-md:leading-[4.57vw] md:px-[0.8748rem] md:text-[0.75rem] md:leading-[1.875rem]';

/**
 * "Popular categories:" heading plus a wrapping row of outline pill tags
 * (Neue Haas Roman 12px uppercase on the 1920 frame). Pills inherit the
 * section's text colour via `border-current`/`text-current` so the component
 * reads on both light and dark sections.
 */
export const CategoryPills = ({
    label,
    items,
    className,
    labelClassName,
    wrapClassName,
    pillClassName,
}: CategoryPillsProps) => (
    <div
        className={cn(
            'flex flex-col max-md:gap-[2.6vw] md:gap-[2.5rem]',
            className,
        )}
    >
        {label && (
            <span
                className={cn(
                    'font-sans leading-none opacity-60 max-md:text-[2.6vw] md:text-[1.248rem]',
                    labelClassName,
                )}
            >
                {label}
            </span>
        )}
        <div
            className={cn(
                // Mobile needs a real 8px gap — the 0.31vw desktop value collapses
                // to ~1px on a phone and the pills read as one run-on blob.
                'flex flex-wrap items-center max-md:gap-x-2 max-md:gap-y-2 md:gap-[0.372rem]',
                wrapClassName,
            )}
        >
            {items.map((item) =>
                item.to ? (
                    <RoccoLink
                        key={item.label}
                        to={item.to}
                        className={cn(pillClass, pillClassName)}
                    >
                        {item.label}
                    </RoccoLink>
                ) : (
                    <span
                        key={item.label}
                        className={cn(pillClass, pillClassName)}
                    >
                        {item.label}
                    </span>
                ),
            )}
        </div>
    </div>
);
